import { useContext, useState, Fragment, useRef, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { MobXProviderContext } from "mobx-react";
import { weekDays } from "utils/constants/onboardingStep";
import MyCalendar from "components/calendar";
import { isPresent } from "utils/helpers/array";
import userStore from "mobXstore/userStore";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toJS } from "mobx";
import { useEarliestDate } from "utils/hooks/useEarliestDate";
import { capitalizeFirstLetter } from "utils/helpers/string";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import {
  useMealNextAvailableDate,
  getValidMealList,
} from "utils/hooks/useMealNextAvailableDate";
import dayjs from "dayjs";
import { Calendar } from "react-multi-date-picker";
import object from "utils/helpers/object";
import classNames from "classnames";

function ChangeEnabledDaysMealPlan({
  visible,
  setModal,
  meals,
  days,
  dates,
  recurringMeals,
}) {
  const { mealManagementStore } = useContext(MobXProviderContext);

  const [warning, setWarning] = useState(false);
  const [isMealDates, setIsMealDates] = useState(false);
  const [defaultSudsidy, setDefaultSubsidy] = useState("");
  const [defaultSubsidyError, setDefaultSubsidyError] = useState(false);

  const nextAvailableMeal = useMealNextAvailableDate(meals);
  const validMeals = getValidMealList(meals);
  const [changeEffect, setChangeEffect] = useState("next");
  const [selectedCustomDate, setSelectedCustomDate] = useState(null);

  const store = useContext(MobXProviderContext);
  const onboardingStore = toJS(store?.onboardingStore);
  const {
    enable_meal_delivery,
    isNotSureSelected,
    default_date,
    meal_allowance_value,
    meal_allowance_type,
  } = onboardingStore;
  const mealPlan = mealManagementStore?.mealPlan;
  const locationID = userStore?.locationID;

  const [defaultDate, setDefaultDate] = useState(default_date || "");
  const [mealDeliveryOption, setMealDeliveryOption] = useState(
    enable_meal_delivery || "every week"
  );
  const [mealRecievingDays, setMealRecievingDays] = useState(days || []);
  const [mealStartDate, setMealStartDate] = useState(
    isNotSureSelected
      ? "I am not sure yet"
      : default_date !== ""
      ? "select date"
      : ""
  );
  const [mealAllowanceType, setMealAllowanceType] = useState(
    meal_allowance_type || ""
  );
  const [mealAllowanceValue, setMealAllowanceValue] = useState(
    meal_allowance_value || 0
  );
  const calendarRef = useRef(null);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const currentMonthIndex = new Date().getMonth();
  const nextMonthIndex = (currentMonthIndex + 1) % 12;
  const nextNextMonthIndex = (currentMonthIndex + 2) % 12;

  const currentMonthName = monthNames[currentMonthIndex];
  const nextMonthName = monthNames[nextMonthIndex];
  const nextNextMonthName = monthNames[nextNextMonthIndex];

  const initialMealSelectedDates = [
    { [currentMonthName]: [] },
    { [nextMonthName]: [] },
    { [nextNextMonthName]: [] },
  ];

  function groupDatesByMonth(dateArray) {
    const monthMap = {};

    // Organize and format dates by month name
    dateArray?.forEach((dateStr) => {
      const dateObj = new Date(dateStr);
      const day = String(dateObj.getDate()).padStart(2, "0");
      const month = String(dateObj.getMonth() + 1).padStart(2, "0");
      const year = dateObj.getFullYear();
      const formattedDate = `${day}-${month}-${year}`;

      const monthName = dateObj.toLocaleString("default", { month: "long" });

      if (!monthMap[monthName]) {
        monthMap[monthName] = [];
      }

      monthMap[monthName].push(formattedDate);
    });

    // Get current, next, and next-next month names
    const today = new Date();
    const monthNames = Array.from({ length: 3 }, (_, i) => {
      const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
      return d.toLocaleString("default", { month: "long" });
    });

    // Construct output in desired format
    const result = monthNames.map((monthName) => ({
      [monthName]: monthMap[monthName] || [],
    }));

    return result;
  }

  const groupedDates = groupDatesByMonth(dates || []);
  const [mealSelectedDates, setMealSelectedDates] = useState(
    groupedDates || initialMealSelectedDates
  );

  let [
    mealSelectedDatesMonth1,
    mealSelectedDatesMonth2,
    mealSelectedDatesMonth3,
  ] = mealSelectedDates;

  let { earliestDate } = useEarliestDate();

  useEffect(() => {
    window.scroll({
      top: 0,
      left: 0,
    });
  }, []);

  useEffect(() => {
    if (mealAllowanceValue === 0 && mealAllowanceType === "daily") {
      handleMealAllowance(20);
    } else if (mealAllowanceValue === 0 && mealAllowanceType === "weekly") {
      handleMealAllowance(100);
    }
  }, [mealAllowanceType, mealAllowanceValue]);

  const handleMealAllowance = (value) => {
    setMealAllowanceValue(value);
  };

  useEffect(() => {
    let isEveryWeekSelected = false;
    let selectedEarliestDate = earliestDate(mealSelectedDates);

    if (mealDeliveryOption?.toLowerCase() == "i will set my own dates") {
      isEveryWeekSelected = false;
    } else if (mealDeliveryOption?.toLowerCase() == "every week") {
      isEveryWeekSelected = true;
    }
    store.onboardingStore.handleMealSeriesAndSubsidiesData(
      isEveryWeekSelected ? mealRecievingDays : [],
      isEveryWeekSelected
        ? mealStartDate == "select date"
          ? defaultDate
          : mealStartDate
        : selectedEarliestDate,
      !isEveryWeekSelected ? mealSelectedDates : [],
      mealAllowanceType,
      mealAllowanceValue,
      mealDeliveryOption
    );
  }, [
    onboardingStore,
    mealRecievingDays,
    mealStartDate,
    mealSelectedDates,
    mealAllowanceType,
    mealAllowanceValue,
    mealDeliveryOption,
    earliestDate,
    defaultDate,
  ]);

  const handleRecievingDaysClick = (value) => {
    if (mealRecievingDays.includes(value)) {
      setMealRecievingDays((prevDays) =>
        prevDays.filter((day) => day !== value)
      );
    } else {
      setMealRecievingDays((prevDays) => [...prevDays, value]);
    }
  };

  const onSavePress = () => {
    if (
      !recurringMeals &&
      !mealPlan?.attributes?.standard_subsidy &&
      !defaultSudsidy
    ) {
      setIsMealDates(true);
    } else if (!warning && object.isPresent(nextAvailableMeal)) {
      // if (recurringMeals && !isPresent(dates)) setWarning(true);
      // else if (isPresent(dates)) setWarning(true);
      setWarning(true);
    } else if (recurringMeals) {
      let payload = {
        meal_series: {
          days: mealRecievingDays?.map((day) => day?.toLowerCase()),
        },
      };

      if (object.isPresent(nextAvailableMeal)) {
        payload.meal_series.effective_date =
          changeEffect == "custom"
            ? selectedCustomDate ?? nextAvailableMeal?.mealDate
            : nextAvailableMeal?.mealDate;
      }

      mealManagementStore
        .updateMealPlan(locationID, mealPlan?.id, payload)
        .then(() => setModal(false));
    } else if (!recurringMeals && isPresent(mealSelectedDates)) {
      let datesToAdd = [];

      mealSelectedDates?.forEach((dateObj) => {
        const [_, dateArray] = Object.entries(dateObj)[0] || [];
        if (Array.isArray(dateArray)) {
          datesToAdd = [...datesToAdd, ...dateArray];
        }
      });

      let payload = {
        meal_series: {
          dates_to_add: datesToAdd,
          standard_subsidy: defaultSudsidy,
        },
      };

      if (object.isPresent(nextAvailableMeal)) {
        payload.meal_series.effective_date =
          changeEffect == "custom"
            ? selectedCustomDate ?? nextAvailableMeal?.mealDate
            : nextAvailableMeal?.mealDate;
      }

      mealManagementStore
        .updateMealPlan(locationID, mealPlan?.id, payload)
        .then(() => setModal(false));
    }
  };

  const handleMultipleDateChange = (newDate) => {
    const updatedMonthsArray = Array.from(mealSelectedDates, (month) => ({
      ...month,
    }));

    if (newDate?.length > 0) {
      let targetIndex = 0;
      const selectedMonthIndex = newDate[0]?.month.number - 1;

      if (selectedMonthIndex === currentMonthIndex) {
        targetIndex = 0;
      } else if (selectedMonthIndex === nextMonthIndex) {
        targetIndex = 1;
      } else if (selectedMonthIndex === nextNextMonthIndex) {
        targetIndex = 2;
      }

      const selectedMonthName = monthNames[selectedMonthIndex];

      const monthIndex = updatedMonthsArray?.findIndex(
        (month) => month && Object.keys(month)[0] === selectedMonthName
      );

      if (monthIndex !== -1) {
        updatedMonthsArray[monthIndex][selectedMonthName] = newDate?.map(
          (date) => `${date.day}-${date.month.number}-${date.year}`
        );
      } else {
        updatedMonthsArray[targetIndex] = {
          [selectedMonthName]: newDate?.map(
            (date) => `${date.day}-${date.month.number}-${date.year}`
          ),
        };
      }

      setMealSelectedDates(updatedMonthsArray);
    }
  };

  const handleDateChange = (value) => {
    const selectedDate = dayjs(value.toDate()).format("YYYY-MM-DD");

    setSelectedCustomDate(selectedDate);
  };

  const onHandleDefaultSubsidy = () => {
    if (!defaultSudsidy) setDefaultSubsidyError(true);
    else {
      onSavePress();
    }
  };

  return (
    <Transition appear show={visible} as={Fragment} avoidClose={true}>
      <Dialog as="div" className="relative z-[90]" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center sm:p-4 md:p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              {warning ? (
                <Dialog.Panel className="min-h-[276px] d-col justify-between rounded-xl transform overflow-hidden sm:rounded-2xl md:rounded-2xl bg-white px-8 pb-8 pt-[24px] text-left align-middle shadow-xl transition-all overflow-visible">
                  <div className="d-col">
                    <div className="flex justify-between items-center mb-[22px]">
                      <button
                        className="font-inter-regular text-[16px] text-hh-accent-dark"
                        onClick={() => {
                          setWarning(false);
                          setSelectedCustomDate(null);
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faChevronLeft}
                          size="xs"
                          className="mr-2"
                          style={{
                            color: "#130f26",
                          }}
                        />
                        Back
                      </button>

                      <button
                        type="button"
                        onClick={() => setModal(false)}
                        className="btn-style"
                      >
                        <FontAwesomeIcon
                          style={{ width: 16, height: 26 }}
                          icon={faXmark}
                          size="2x"
                          color="#BBC0C5"
                        />
                      </button>
                    </div>

                    <h2 className="text-2xl font-inter-bold text-dark-gray mb-4">
                      Set Date for Changes
                    </h2>

                    <div className="rounded-lg bg-light-gray py-[12px] px-[8px] d-col">
                      {recurringMeals ? (
                        <small className="font-inter-regular text-dark-gray text-[16px] mt-[16px]">
                          Recurring weekly meals on{" "}
                          {mealRecievingDays
                            .map(
                              (day) =>
                                day.charAt(0).toUpperCase() + day.slice(1)
                            )
                            .join(", ")}
                          .
                        </small>
                      ) : (
                        <small className="font-inter-regular text-dark-gray text-[16px] mt-[16px]">
                          One-Time meals on{" "}
                          {mealSelectedDates
                            .flatMap((monthObj) =>
                              Object.values(monthObj).flat()
                            )
                            .join(", ")}
                        </small>
                      )}

                      <small className="font-inter-regular text-dark-gray text-[16px] mt-[14px]">
                        These changes will apply to all future meals in the
                        series.
                      </small>
                    </div>

                    <strong className="text-dark-gray font-inter-semibold text-[16px] my-[16px]">
                      When should they take effect?
                    </strong>

                    <div className="d-col rounded-lg">
                      <div key="next" className="flex items-center">
                        <input
                          name="date-method"
                          type="radio"
                          value="next"
                          checked={changeEffect == "next"}
                          onChange={(e) => setChangeEffect(e.target.value)}
                          className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                        <label
                          htmlFor="photo"
                          className="ml-3 block text-sm font-inter-regular leading-6 text-gray-900"
                        >
                          Next available meal in series:{" "}
                          {nextAvailableMeal?.mealDate}
                        </label>
                      </div>

                      <div key="custom" className="flex items-center mt-[16px]">
                        <input
                          name="date-method"
                          type="radio"
                          value={"custom"}
                          checked={changeEffect == "custom"}
                          onChange={(e) => setChangeEffect(e.target.value)}
                          className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                        <label
                          htmlFor="signature"
                          className="ml-3 block text-sm font-inter-regular leading-6 text-gray-900"
                        >
                          Pick a custom date
                        </label>
                      </div>
                    </div>

                    {changeEffect == "custom" && (
                      <div
                        className="flex flex-row justify-center items-center mt-[16px]"
                        ref={calendarRef}
                      >
                        <Calendar
                          highlightToday={true}
                          className="shadow-lg"
                          isMulti={false}
                          onChange={handleDateChange}
                          disableYearPicker={true}
                          monthYearSeparator={" "}
                          mapDays={({ date }) => {
                            const allowedDates = validMeals?.map(
                              (meal) => meal?.meal_date
                            );
                            const formatted = dayjs(date.toDate()).format(
                              "YYYY-MM-DD"
                            );
                            const isEnabled = allowedDates.includes(formatted);

                            return {
                              disabled: !isEnabled,
                              style: isEnabled
                                ? { color: "#000" }
                                : {
                                    color: "#ccc",
                                    textDecoration: "",
                                    pointerEvents: "none",
                                  },
                            };
                          }}
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex justify-center items-center mt-5">
                    <button
                      className="btn-outline btn-style w-full"
                      onClick={() => setModal(false)}
                    >
                      Cancel
                    </button>

                    <button
                      className={classNames({
                        "btn-purple btn-style px-6 ml-3 w-full": true,
                        "btn-disabled":
                          changeEffect === "custom" && !selectedCustomDate,
                      })}
                      onClick={onSavePress}
                      disabled={changeEffect == "custom" && !selectedCustomDate}
                    >
                      Save
                    </button>
                  </div>
                </Dialog.Panel>
              ) : isMealDates ? (
                <Dialog.Panel className="min-h-[276px] d-col justify-between rounded-xl transform overflow-hidden sm:rounded-2xl md:rounded-2xl bg-white px-8 pb-8 pt-[24px] text-left align-middle shadow-xl transition-all overflow-visible">
                  <div className="d-col">
                    <div className="d-row justify-between items-center">
                      <h2 className="text-3xl font-inter-bold text-neutral-700">
                        {" "}
                        Set Default Subsidy For Dates
                      </h2>
                      <button
                        onClick={() => setModal(false)}
                        className="text-dark-gray hover:text-yellow-600"
                        aria-label="Close"
                      >
                        <FontAwesomeIcon
                          style={{ width: 16, height: 26 }}
                          icon={faXmark}
                          size="xl"
                          color="#BBC0C5"
                        />
                      </button>
                    </div>

                    <small className="w-[560px] text-slate-500 font-inter-regular text-sm mt-[24px]">
                      Setting a subsidy gives users this subsidy amount for each
                      meal. If you want to set a different subsidy for a
                      specific day, you can do so by managing that meal directly
                      on the date you want to update.
                    </small>

                    <div className="d-col bg-light-gray w-full p-[12px] rounded-lg mt-[24px]">
                      <div
                        className={classNames(
                          "focus:outline-none py-[9px] px-[19px] rounded-md",
                          defaultSubsidyError
                            ? "bg-white border border-red-500"
                            : "bg-white"
                        )}
                      >
                        <span
                          className={`text-md ${
                            defaultSudsidy ? "text-black" : "text-gray-400"
                          }`}
                        >
                          $
                        </span>

                        <input
                          className="text-[16px] font-inter-medium bg-transparent h-full focus:outline-none text-black"
                          type="text"
                          placeholder="0.00"
                          onFocus={() => {
                            if (
                              defaultSudsidy == "0.0" ||
                              defaultSudsidy == 0.0
                            ) {
                              setDefaultSubsidy("");
                            }
                          }}
                          onChange={(e) => {
                            const value = e.target.value;
                            const regex = /^[0-9]*\.?[0-9]*$/;
                            if (regex.test(value)) {
                              setDefaultSubsidy(value);
                              setDefaultSubsidyError(false);
                            }
                          }}
                          value={defaultSudsidy}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-center items-center mt-6">
                    <button
                      className="btn-outline btn-style"
                      onClick={() => setModal(false)}
                    >
                      Cancel
                    </button>

                    <button
                      className="btn-purple btn-style px-6 ml-3"
                      onClick={onHandleDefaultSubsidy}
                    >
                      Save
                    </button>
                  </div>
                </Dialog.Panel>
              ) : (
                <Dialog.Panel className=" d-col justify-between rounded-xl  transform overflow-hidden sm:rounded-2xl md:rounded-2xl bg-white px-8 pb-8 pt-[24px] text-left align-middle shadow-xl transition-all">
                  <div className="d-row justify-between items-center">
                    <h2 className="text-2xl font-inter-bold">Meal Schedule</h2>
                    <button
                      onClick={() => setModal(false)}
                      className="text-dark-gray hover:text-yellow-600"
                      aria-label="Close"
                    >
                      <FontAwesomeIcon
                        style={{ width: 16, height: 26 }}
                        icon={faXmark}
                        size="2x"
                        color="#BBC0C5"
                      />
                    </button>
                  </div>

                  {recurringMeals && (
                    <>
                      <div class="justify-start text-neutral-700 text-base font-inter-semibold leading-tight mt-6">
                        Recurring Weekly Meals
                      </div>
                      <small className="input-label mt-2">
                        Select the days you'd like to receive meals every week.
                      </small>
                      <div className="mb-0.5 w-full xs:w-full-215 mt-[8px]">
                        <div className="lg:inline-flex md:inline-flex">
                          {weekDays?.map((value, index) => (
                            <div
                              key={index}
                              className={`lg:mr-3 md:mr-3 mt-4 lg:mt-0 md:mt-0 px-4 py-3 hover:cursor-pointer text-slate-700 font-inter-medium text-sm leading-[14px] ${
                                mealRecievingDays.some((day) => day == value)
                                  ? "bg-indigo-500 text-white "
                                  : "bg-slate-50 hover:bg-indigo-500 hover:text-white "
                              } rounded-md justify-center gap-2 flex`}
                              onClick={() => handleRecievingDaysClick(value)}
                            >
                              {capitalizeFirstLetter(value?.slice(0, 3))}
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {!recurringMeals && (
                    <>
                      <div class="justify-start text-neutral-700 text-base font-inter-semibold leading-tight mt-[24px]">
                        One-Time Meals
                      </div>

                      <div class="w-[790px] self-stretch justify-start text-slate-700 text-sm font-inter-normal leading-tight mt-2">
                        Choose specific dates to schedule meals. Selecting dates
                        here will add meals to your existing schedule and it
                        won’t replace any regular days.
                      </div>

                      <div className="flex inline-flex gap-5 overflow-x-auto mt-3">
                        <MyCalendar
                          highlightToday={false}
                          disableMonthYearPicker={true}
                          isMultiple={true}
                          showButtons={false}
                          handleMultipleDates={handleMultipleDateChange}
                          value={1}
                          multipleValues={mealSelectedDatesMonth1}
                          isOwnDates={true}
                        />

                        <MyCalendar
                          highlightToday={false}
                          disableMonthYearPicker={true}
                          isMultiple={true}
                          showButtons={false}
                          handleMultipleDates={handleMultipleDateChange}
                          value={2}
                          multipleValues={mealSelectedDatesMonth2}
                          isOwnDates={true}
                        />

                        <MyCalendar
                          highlightToday={false}
                          disableMonthYearPicker={true}
                          isMultiple={true}
                          showButtons={false}
                          handleMultipleDates={handleMultipleDateChange}
                          value={3}
                          multipleValues={mealSelectedDatesMonth3}
                          isOwnDates={true}
                        />
                      </div>
                    </>
                  )}

                  <div className="d-row w-full items-center mt-[24px] justify-center">
                    <button
                      className="btn-light btn-style py-3 px-5"
                      onClick={() => setModal(false)}
                    >
                      Cancel
                    </button>

                    <button
                      className="btn-purple btn-style py-3 px-6 ml-3"
                      onClick={onSavePress}
                    >
                      Next
                    </button>
                  </div>
                </Dialog.Panel>
              )}
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default ChangeEnabledDaysMealPlan;
